// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\maternite\MaterniteReportPage.js

import React, { useState, useEffect, useCallback } from "react";
import { 
  Form, Input, Select, DatePicker, Button, Row, Col, Divider, 
  message, Typography, InputNumber, Grid, List, Card, Badge, Tag, Tabs, Checkbox
} from "antd";
import { 
  RocketOutlined, SmileOutlined, 
  EnvironmentOutlined, GiftOutlined, HistoryOutlined, 
  LogoutOutlined, PlusOutlined, DeleteOutlined, UserOutlined, MedicineBoxOutlined 
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../services/axiosServicesVisit";
import dayjs from "dayjs";

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ID_COFFRET_BABYWORLD = 200; 

const MaterniteReportPage = () => {
  const screens = useBreakpoint();
  const history = useHistory();
  const [form] = Form.useForm();
  
  // Surveillance des champs pour l'affichage dynamique
  const noMomInfo = Form.useWatch('noMomInfo', form);
  const selectedFunction = Form.useWatch('professionalFunction', form);
  
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [activeTab, setActiveTab] = useState("BIRTH");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    history.push("/sign-in");
    window.location.reload();
  };

  const fetchMyHistory = useCallback(async () => {
    try {
      const twentyFourHoursAgo = dayjs().subtract(24, "hours").format("YYYY-MM-DD HH:mm:ss");
      const res = await axiosInstance.get("/v1/report-clinics", {
        params: { 
            startDate: twentyFourHoursAgo, 
            limit: 50, 
            isMobile: 'true' 
        }
      });
      setRecentReports(res.data.data || []);
    } catch (e) {
      console.error("Erreur historique");
    }
  }, []);

  useEffect(() => {
    axiosInstance.get("/v1/report-clinics/clinics-list").then(res => setClinics(res.data || []));
    fetchMyHistory();
  }, [fetchMyHistory]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        type: activeTab,
        clinicId: values.clinicId,
        visitDate: values.visitDate.format("YYYY-MM-DD"),
        comment: values.comment,
        reportSamples: [{ sampleId: ID_COFFRET_BABYWORLD, quantity: values.coffretQuantity }]
      };

      if (activeTab === "BIRTH") {
        payload.momName = values.noMomInfo ? "" : values.momName;
        payload.momPhone = values.noMomInfo ? "" : values.momPhone;
        payload.momEmail = values.noMomInfo ? "" : values.momEmail;
        payload.momAddress = values.noMomInfo ? "" : values.momAddress;
        
        payload.babies = values.babies.map(b => ({
          name: b.name,
          dob: b.dob.format("YYYY-MM-DD"),
          gender: b.gender
        }));
      } else {
        payload.professionalName = values.professionalName;
        // Si "Autre" est choisi, on prend la valeur du champ texte customFunction
        payload.professionalFunction = values.professionalFunction === 'Autre' 
            ? values.customFunction 
            : values.professionalFunction;
      }

      await axiosInstance.post("/v1/report-clinics", payload);
      message.success("Rapport enregistré avec succès !");
      
      // Réinitialisation complète
      form.resetFields([
          'babies', 'momName', 'momPhone', 'momEmail', 'momAddress', 'noMomInfo', 
          'professionalName', 'professionalFunction', 'customFunction', 'comment'
      ]);
      fetchMyHistory(); 
    } catch (error) {
      message.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh', backgroundColor: '#f0f2f5', padding: screens.lg ? '40px 20px' : '10px' }}>
      
      <div style={{ maxWidth: '900px', margin: '0 auto 15px auto', display: 'flex', justifyContent: 'flex-end' }}>
        <Button danger ghost icon={<LogoutOutlined />} onClick={handleLogout} style={{ fontWeight: 'bold', borderRadius: '8px' }}>
          DÉCONNEXION
        </Button>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', backgroundColor: '#fff', padding: '30px' , borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        
        <Title level={2} style={{ color: '#0e519b', textAlign: 'center' }}><RocketOutlined /> ESPACE CLINIQUE</Title>

        <Form 
            form={form} 
            layout="vertical" 
            onFinish={onFinish} 
            initialValues={{ coffretQuantity: 1, babies: [{ name: '', dob: dayjs(), gender: 'M' }], noMomInfo: false }}
        >
          
          <Row gutter={16}>
            <Col xs={24} lg={12}>
              <Form.Item name="visitDate" label="Date de la visite" rules={[{ required: true }]} initialValue={dayjs()}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item name="clinicId" label="Clinique" rules={[{ required: true }]}>
                <Select placeholder="Choisir la clinique" showSearch optionFilterProp="children">
                  {clinics.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
            {/* ONGLET NAISSANCES */}
            <Tabs.TabPane tab={<span><SmileOutlined /> NAISSANCES</span>} key="BIRTH">
                <Form.List name="babies">
                    {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...restField }) => (
                        <Card size="small" key={key} style={{ marginBottom: 15, border: '1px dashed #adc6ff' }} title={`Enfant n°${name + 1}`} extra={fields.length > 1 && <Button type="link" danger onClick={() => remove(name)} icon={<DeleteOutlined />} />}>
                            <Row gutter={16}>
                                <Col xs={24} lg={12}><Form.Item {...restField} name={[name, 'name']} label="Nom du bébé" rules={[{ required: activeTab === 'BIRTH' }]}><Input /></Form.Item></Col>
                                <Col xs={12} lg={6}><Form.Item {...restField} name={[name, 'dob']} label="Né le" rules={[{ required: activeTab === 'BIRTH' }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                                <Col xs={12} lg={6}><Form.Item {...restField} name={[name, 'gender']} label="Sexe" rules={[{ required: activeTab === 'BIRTH' }]}><Select><Option value="M">GARÇON</Option><Option value="F">FILLE</Option></Select></Form.Item></Col>
                            </Row>
                        </Card>
                        ))}
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginBottom: 20 }}>Ajouter un bébé</Button>
                    </>
                    )}
                </Form.List>
                
                <Divider orientation="left" style={{color:'#0e519b'}}>MAMAN</Divider>
                
                <Form.Item name="noMomInfo" valuePropName="checked" style={{ marginBottom: 15 }}>
                    <Checkbox style={{ fontWeight: 'bold' }}>La maman ne souhaite pas donner ses informations</Checkbox>
                </Form.Item>

                {!noMomInfo && (
                    <Row gutter={16}>
                        <Col xs={24} lg={12}><Form.Item name="momName" label="Nom & Prénom"><Input placeholder="Optionnel" /></Form.Item></Col>
                        <Col xs={24} lg={12}><Form.Item name="momPhone" label="Téléphone"><Input placeholder="Optionnel" /></Form.Item></Col>
                        <Col xs={24} lg={12}><Form.Item name="momEmail" label="Email" rules={[{ type: 'email', message: 'Format invalide' }]}><Input placeholder="exemple@mail.com" /></Form.Item></Col>
                        <Col xs={24} lg={12}><Form.Item name="momAddress" label="Adresse (Ville/Quartier)"><Input prefix={<EnvironmentOutlined />} placeholder="Ex: Ennasr, Ariana..." /></Form.Item></Col>
                    </Row>
                )}
            </Tabs.TabPane>

            {/* ONGLET PROFESSIONNEL */}
            <Tabs.TabPane tab={<span><UserOutlined /> PROFESSIONNEL</span>} key="PROFESSIONAL">
                <Row gutter={16}>
                    <Col xs={24} lg={12}>
                        <Form.Item name="professionalName" label="Nom du professionnel" rules={[{ required: activeTab === 'PROFESSIONAL' }]}>
                            <Input placeholder="Nom du contact" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Form.Item name="professionalFunction" label="Fonction" rules={[{ required: activeTab === 'PROFESSIONAL' }]}>
                            <Select placeholder="Sélectionner">
                                <Option value="Sage-femme">Sage-femme</Option>
                                <Option value="Major de service">Major</Option>
                                <Option value="Gynécologue">Gynécologue</Option>
                                <Option value="Autre">Autre</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Champ dynamique si "Autre" est sélectionné */}
                    {selectedFunction === 'Autre' && (
                        <Col xs={24} lg={24}>
                            <Form.Item 
                                name="customFunction" 
                                label="Précisez la fonction" 
                                rules={[{ required: true, message: 'Veuillez saisir la fonction' }]}
                            >
                                <Input placeholder="Saisir la fonction manuellement..." />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </Tabs.TabPane>
          </Tabs>

          <Divider orientation="left"><GiftOutlined /> COFFRET BABYWORLD</Divider>
          <div style={{ textAlign: 'center', background: '#f9f9f9', padding: '15px', borderRadius: '8px', border: '1px solid #0e519b', marginBottom: 20 }}>
            <Text strong>Quantité à distribuer :</Text>
            <Form.Item name="coffretQuantity" noStyle><InputNumber min={1} style={{ marginLeft: 15, width: 80 }} /></Form.Item>
          </div>

          <Form.Item name="comment" label="Commentaire"><Input.TextArea rows={2} /></Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={loading} style={{ height: 60, fontSize: '20px', fontWeight: 'bold', backgroundColor: '#0e519b', borderRadius: 10 }}>ENVOYER LE RAPPORT</Button>
        </Form>

        <Divider orientation="left" style={{ marginTop: 40, borderColor: '#0e519b' }}>
            <HistoryOutlined /> ENVOIS DES DERNIÈRES 24H
        </Divider>
        <List
            dataSource={recentReports}
            locale={{ emptyText: "Aucun envoi depuis 24h" }}
            renderItem={(item) => (
                <Card size="small" style={{ marginBottom: 12, borderRadius: 10, borderLeft: item.type === 'PROFESSIONAL' ? '5px solid #52c41a' : '5px solid #0e519b' }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Text strong style={{ fontSize: 15 }}>
                                {item.type === 'PROFESSIONAL' ? (
                                    <><MedicineBoxOutlined style={{color:'#52c41a'}} /> {item.professionalName}</>
                                ) : (
                                    <><SmileOutlined style={{color:'#0e519b'}} /> {item.babyName}</>
                                )}
                            </Text>
                            <div style={{ fontSize: 12, color: '#666' }}><EnvironmentOutlined /> {item.clinic?.name}</div>
                        </Col>
                        <Col style={{ textAlign: 'right' }}>
                            <Badge status={item.type === 'PROFESSIONAL' ? "success" : "processing"} text={dayjs(item.created_at).format("HH:mm")} />
                            <div style={{ marginTop: 4 }}>
                                <Tag color={item.type === 'PROFESSIONAL' ? "green" : "blue"}>{item.type}</Tag>
                                <Tag color="default">x{item.rcSamples?.[0]?.quantity || 0}</Tag>
                            </div>
                        </Col>
                    </Row>
                </Card>
            )}
        />
      </div>
    </div>
  );
};

export default MaterniteReportPage;